import React, { useEffect, useRef } from "react";

// Pairs with usePaginatedList — the mode/page-size radio buttons and
// dropdown at the top (so a viewer can change how THEY want to browse
// this particular list, regardless of root's configured default), plus
// either numbered-page navigation or an infinite-scroll sentinel at the
// bottom, depending on which mode is active. listKey is only used to make
// the radio button `name` attributes unique if more than one of these
// renders on the same page at once.
export default function ListDisplayControls({
  listKey,
  mode,
  setMode,
  pageSize,
  setPageSize,
  page,
  setPage,
  totalPages,
  totalMatching,
  itemsCount,
  loading,
  loadMore,
  hasMore,
}) {
  return (
    <div>
      <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", marginBottom: 10, fontSize: 13 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="radio" name={`${listKey}-display-mode`} checked={mode === "pagination"} onChange={() => setMode("pagination")} />
          Pagination
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="radio" name={`${listKey}-display-mode`} checked={mode === "infinite"} onChange={() => setMode("infinite")} />
          Infinite Scroll
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {mode === "pagination" ? "Records per page" : "Records per batch"}
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ padding: 4 }}>
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {mode === "pagination" ? (
        <PaginationNav page={page} setPage={setPage} totalPages={totalPages} totalMatching={totalMatching} loading={loading} />
      ) : (
        <InfiniteScrollFooter itemsCount={itemsCount} totalMatching={totalMatching} loading={loading} loadMore={loadMore} hasMore={hasMore} />
      )}
    </div>
  );
}

function PaginationNav({ page, setPage, totalPages, totalMatching, loading }) {
  if (totalMatching === 0) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, marginBottom: 12 }}>
      <button onClick={() => setPage(1)} disabled={loading || page <= 1}>
        « First
      </button>
      <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={loading || page <= 1}>
        ‹ Prev
      </button>
      <span style={{ opacity: 0.7 }}>
        Page {page} of {totalPages} ({totalMatching} total)
      </span>
      <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={loading || page >= totalPages}>
        Next ›
      </button>
      <button onClick={() => setPage(totalPages)} disabled={loading || page >= totalPages}>
        Last »
      </button>
    </div>
  );
}

function InfiniteScrollFooter({ itemsCount, totalMatching, loading, loadMore, hasMore }) {
  const sentinelRef = useRef(null);

  // Fires loadMore automatically once the sentinel scrolls into view,
  // rather than requiring a manual "load more" click — this is what
  // makes it genuinely "infinite scroll" rather than a paged list with
  // extra steps.
  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" } // starts loading a bit before it's actually visible, so it feels seamless
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadMore]);

  return (
    <div style={{ fontSize: 13, marginBottom: 12 }}>
      <div style={{ opacity: 0.6, marginBottom: 6 }}>
        Showing {itemsCount} of {totalMatching}
      </div>
      {hasMore && (
        <div ref={sentinelRef} style={{ textAlign: "center", padding: 10, opacity: 0.6 }}>
          {loading ? "Loading more…" : "Scroll for more"}
        </div>
      )}
    </div>
  );
}
